import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Download, FileText, Table } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface TeamReportExportProps {
  companyId: string;
  companyName: string;
}

export function TeamReportExport({ companyId, companyName }: TeamReportExportProps) {
  const [isExporting, setIsExporting] = useState(false);

  const fetchCompanyData = async () => {
    // Fetch team performance
    const { data: teamData } = await supabase
      .from('team_performance_cache')
      .select('*')
      .eq('company_id', companyId)
      .order('average_score', { ascending: false });

    // Fetch recent evaluations
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    
    const { data: evaluations } = await supabase
      .from('simulation_evaluations')
      .select('*')
      .eq('company_id', companyId)
      .gte('created_at', thirtyDaysAgo.toISOString())
      .eq('is_submitted', true)
      .is('deleted_at', null)
      .order('created_at', { ascending: false });

    return { teamData: teamData || [], evaluations: evaluations || [] };
  };

  const exportTeamSummary = async () => {
    setIsExporting(true);
    try {
      const { teamData, evaluations } = await fetchCompanyData();

      const totalSessions = teamData.reduce((sum, m) => sum + (m.total_sessions || 0), 0);
      const avgScore = teamData.length > 0
        ? Math.round(teamData.reduce((sum, m) => sum + (m.average_score || 0), 0) / teamData.length)
        : 0;

      const lines = [
        `${companyName} - Team Performance Report`,
        `Generated: ${new Date().toLocaleString()}`,
        '',
        'SUMMARY',
        'Metric,Value',
        `Team Members,${teamData.length}`,
        `Total Sessions (All Time),${totalSessions}`,
        `Team Average Score,${avgScore}%`,
        `Sessions (Last 30 Days),${evaluations.length}`,
        '',
        'TEAM MEMBER PERFORMANCE',
        'Name,Email,Sessions,Best Score,Avg Score,Last Session',
        ...teamData.map(m => 
          `"${m.user_name || 'Unknown'}",${m.user_email || ''},${m.total_sessions || 0},${m.best_score || 0}%,${Math.round(m.average_score || 0)}%,${m.last_session_date ? new Date(m.last_session_date).toLocaleDateString() : 'Never'}`
        ),
      ];

      downloadCSV(lines.join('\n'), `${companyName}-team-summary-${new Date().toISOString().split('T')[0]}.csv`);
      toast.success('Team summary exported');
    } catch (error) {
      toast.error('Failed to export');
    } finally {
      setIsExporting(false);
    }
  };

  const exportDetailedReport = async () => {
    setIsExporting(true);
    try {
      const { evaluations } = await fetchCompanyData();

      const headers = ['Date', 'User', 'Email', 'Agent', 'Score', 'Type'];
      const rows = evaluations.map(e => [
        new Date(e.created_at).toLocaleDateString(),
        `"${e.user_name || 'Unknown'}"`,
        e.user_email || '',
        `"${e.agent_name || 'Unknown'}"`,
        `${e.overall_score || 0}%`,
        e.agent_type || '',
      ]);

      const csvContent = [
        `${companyName} - Detailed Session Report (Last 30 Days)`,
        `Generated: ${new Date().toLocaleString()}`,
        '',
        headers.join(','),
        ...rows.map(r => r.join(',')),
      ].join('\n');

      downloadCSV(csvContent, `${companyName}-sessions-${new Date().toISOString().split('T')[0]}.csv`);
      toast.success('Detailed report exported');
    } catch (error) {
      toast.error('Failed to export');
    } finally {
      setIsExporting(false);
    }
  };

  const downloadCSV = (content: string, filename: string) => {
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="gap-2" disabled={isExporting}>
          <Download className="h-4 w-4" />
          {isExporting ? 'Exporting...' : 'Export'}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuLabel>Export Reports</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={exportTeamSummary} className="gap-2">
          <FileText className="h-4 w-4" />
          Team Summary
        </DropdownMenuItem>
        <DropdownMenuItem onClick={exportDetailedReport} className="gap-2">
          <Table className="h-4 w-4" />
          All Sessions
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
